import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { firstAidGuides } from "@/mocks/firstAid";
import useReveal from "@/hooks/useReveal";

interface FirstAidGuide {
  id: string;
  title: string;
  icon: string;
  summary: string;
  steps: string[];
  avoid: string[];
}

const guides = firstAidGuides as FirstAidGuide[];

export default function FirstAidBasics() {
  const [openId, setOpenId] = useState(guides[0]?.id ?? "");
  const reveal = useReveal<HTMLDivElement>(0.05);

  const toggle = (id: string) => setOpenId((current) => (current === id ? "" : id));

  return (
    <section className="border-t border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="While help is on the way"
          title="First aid basics"
          description="Simple, calm steps for the most common emergencies. Tap a card to open it - always follow the instructions of emergency responders once they arrive."
        />

        <div
          ref={reveal.ref}
          className={`mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50 ${
            reveal.visible ? "animate-fade-up" : "opacity-0"
          }`}
        >
          {guides.map((guide) => {
            const open = openId === guide.id;
            return (
              <article key={guide.id} className="border-b border-background-200 last:border-b-0">
                <h4>
                  <button
                    type="button"
                    onClick={() => toggle(guide.id)}
                    aria-expanded={open}
                    aria-controls={`${guide.id}-panel`}
                    className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-background-100 md:px-6 md:py-5"
                  >
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-red-50 text-red-600">
                      <i className={`${guide.icon} text-xl`} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-heading text-base font-bold text-foreground-950">
                        {guide.title}
                      </span>
                      <span className="mt-0.5 block truncate text-sm text-foreground-600">
                        {guide.summary}
                      </span>
                    </span>
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-background-300 text-foreground-700">
                      <i
                        className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                          open ? "rotate-180" : ""
                        }`}
                      />
                    </span>
                  </button>
                </h4>

                <div
                  id={`${guide.id}-panel`}
                  hidden={!open}
                  className="border-t border-background-200 bg-background-100 px-4 py-5 md:px-6"
                >
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <ol className="flex flex-col gap-3">
                      {guide.steps.map((step, index) => (
                        <li key={`${guide.id}-step-${index}`} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-500 font-heading text-xs font-bold text-background-50">
                            {index + 1}
                          </span>
                          <span className="text-sm leading-relaxed text-foreground-700">
                            {step}
                          </span>
                        </li>
                      ))}
                    </ol>

                    <div className="h-fit rounded-lg border border-accent-200 bg-accent-50 p-4">
                      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-accent-800">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-alert-line text-base" />
                        </span>
                        Important
                      </span>
                      <ul className="mt-3 flex flex-col gap-2">
                        {guide.avoid.map((note, index) => (
                          <li
                            key={`${guide.id}-avoid-${index}`}
                            className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700"
                          >
                            <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center text-red-600">
                              <i className="ri-close-circle-line text-sm" />
                            </span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center">
            <i className="ri-information-line text-sm" />
          </span>
          This is general guidance only and does not replace professional medical advice. In any
          life-threatening situation, call 112 first.
        </p>
      </div>
    </section>
  );
}